<template>
  <div class="relatorios">
    <header class="cabecalho">
      <div>
        <h2>Relatórios do módulo</h2>
        <p>
          Gere um panorama consolidado dos PDMs, projetos, ações e KPIs
          acompanhados.
        </p>
      </div>
    </header>

    <div class="filtros-container">
      <div class="filtros-header">
        <div>
          <h3>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 7h18M10 11h4"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
              <path
                d="M21 4H3a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h18a1 1 0 0 0-1-1V5a1 1 0 0 0-1-1z"
                stroke="currentColor"
                stroke-width="1.5"
              />
            </svg>
            Filtros
          </h3>
          <p>Refine a visualização dos dados por critérios específicos</p>
        </div>
        <button
          type="button"
          class="filtros-clear"
          @click="limparFiltros"
          v-if="municipioFiltro.municipio"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 18L18 6M6 6l12 12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Limpar filtros
        </button>
      </div>
      <div class="filtros-grid">
        <div class="filtro-campo">
          <label for="filtro-municipio">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"
                stroke="currentColor"
                stroke-width="1.5"
              />
              <circle
                cx="12"
                cy="10"
                r="3"
                stroke="currentColor"
                stroke-width="1.5"
              />
            </svg>
            Município
          </label>
          <div class="filtro-input-wrapper">
            <ComboboxMunicipio
              ref="filtroMunicipio"
              :filter="municipioFiltro"
              class="combobox-municipio"
              clearable
              @changeCity="aoAlterarMunicipio"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Sistema de Abas -->
    <div class="relatorios-tabs">
      <nav class="tabs-nav">
        <button
          type="button"
          class="tab-btn"
          :class="{ active: abaAtiva === 'panorama' }"
          @click="alternarAba('panorama')"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"
            />
          </svg>
          Panorama Geral
          <span v-if="relatorio.length" class="tab-badge">{{
            relatorio.length
          }}</span>
        </button>
        <button
          type="button"
          class="tab-btn"
          :class="{ active: abaAtiva === 'progresso' }"
          @click="alternarAba('progresso')"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M13,2.05V5.08C16.39,5.57 19,8.47 19,12C19,12.9 18.82,13.75 18.5,14.54L21.12,16.07C21.68,14.83 22,13.45 22,12C22,6.82 18.05,2.55 13,2.05M12,19C8.13,19 5,15.87 5,12C5,8.47 7.61,5.57 11,5.08V2.05C5.94,2.55 2,6.81 2,12C2,17.52 6.48,22 12,22C13.19,22 14.3,21.75 15.31,21.3L13.5,18.62C13.04,18.86 12.53,19 12,19Z"
            />
          </svg>
          Progresso Detalhado
          <span v-if="relatorioProgresso.length" class="tab-badge">{{
            relatorioProgresso.length
          }}</span>
        </button>
      </nav>

      <!-- Ações da Aba Ativa -->
      <div class="tabs-actions">
        <button
          v-if="abaAtiva === 'panorama'"
          type="button"
          class="botao-primario"
          :disabled="carregando"
          @click="carregarRelatorio"
        >
          <svg
            v-if="!carregando"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path
              d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"
            />
          </svg>
          <svg
            v-else
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="animate-spin"
          >
            <path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z" />
          </svg>
          {{ carregando ? "Gerando..." : "Gerar Panorama" }}
        </button>
        <button
          v-if="abaAtiva === 'progresso'"
          type="button"
          class="botao-secundario"
          :disabled="carregandoProgresso"
          @click="carregarRelatorioProgresso"
        >
          <svg
            v-if="!carregandoProgresso"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path
              d="M13,2.05V5.08C16.39,5.57 19,8.47 19,12C19,15.53 16.39,18.43 13,18.92V21.95C18.05,21.45 22,17.19 22,12C22,6.82 18.05,2.55 13,2.05M11,21.95V18.92C7.61,18.43 5,15.53 5,12C5,8.47 7.61,5.57 11,5.08V2.05C5.95,2.55 2,6.81 2,12C2,17.52 6.48,22 12,22C11.66,22 11.33,21.98 11,21.95Z"
            />
          </svg>
          <svg
            v-else
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="animate-spin"
          >
            <path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z" />
          </svg>
          {{ carregandoProgresso ? "Gerando..." : "Gerar Progresso" }}
        </button>
      </div>
    </div>

    <section v-if="erro" class="estado erro">{{ erro }}</section>
    <section v-else-if="carregando" class="estado">
      Carregando dados do relatório...
    </section>
    <section
      v-else-if="relatorioOrdenado.length"
      class="painel"
      v-show="abaAtiva === 'panorama'"
    >
      <header class="painel-toolbar">
        <!-- Aba Panorama -->
        <div v-show="abaAtiva === 'panorama'" class="tab-content">
          <!-- Indicador de Filtro Ativo -->
          <div
            v-if="relatorio.length || carregando || erro"
            class="filtro-ativo-indicator"
          >
            <div class="filtro-info">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M14,12V19.88C14.04,20.18 13.94,20.5 13.71,20.71C13.32,21.1 12.69,21.1 12.3,20.71L10.29,18.7C10.06,18.47 9.96,18.16 10,17.87V12H9.97L4.21,4.62C3.87,4.19 3.95,3.56 4.38,3.22C4.57,3.08 4.78,3 5,3V3H19V3C19.22,3 19.43,3.08 19.62,3.22C20.05,3.56 20.13,4.19 19.79,4.62L14.03,12H14Z"
                />
              </svg>
              <span class="filtro-label">Filtrando por:</span>
              <span class="filtro-valor">{{ municipioFiltroTexto }}</span>

              <div>
                <button
                  type="button"
                  class="botao-exportar"
                  @click="exportarProgressoXls"
                >
                  Exportar XLS
                </button>
                <button
                  type="button"
                  class="botao-exportar"
                  @click="exportarProgressoPdf"
                >
                  Exportar PDF
                </button>
              </div>
            </div>
            <div v-if="relatorio.length" class="resultados-count">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 17H7V15H17V17ZM17 13H7V11H17V13ZM17 9H7V7H17V9Z"
                />
              </svg>
              {{ relatorio.length }} PDM{{
                relatorio.length !== 1 ? "s" : ""
              }}
              encontrado{{ relatorio.length !== 1 ? "s" : "" }}
            </div>
          </div>

          <section v-if="erro" class="estado erro">{{ erro }}</section>
          <section v-else-if="carregando" class="estado">
            Carregando dados do relatório...
          </section>
          <article
            v-for="pdm in relatorioOrdenado"
            :key="pdm.idPdm"
            class="cartao-pdm"
          >
            <details
              :data-panorama-pdm-key="chavePanoramaPdm(pdm)"
              data-role="panorama-pdm"
            >
              <summary class="cartao-pdm__cabecalho">
                <div>
                  <h3>{{ pdm.nome }}</h3>
                  <p class="subtitulo">{{ resumoPdm(pdm) }}</p>
                </div>
                <dl class="cartao-pdm__metadados">
                  <div>
                    <dt>Período</dt>
                    <dd>
                      {{ formatarPeriodo(pdm.periodoInicio, pdm.periodoFim) }}
                    </dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>{{ pdm.status || "Não informado" }}</dd>
                  </div>
                  <div>
                    <dt>Projetos</dt>
                    <dd>{{ pdm.projetos.length }}</dd>
                  </div>
                  <div>
                    <dt>Progresso OKR</dt>
                    <dd>{{ formatarPercentual(pdm.progressoOkr || 0) }}</dd>
                  </div>
                </dl>
              </summary>

              <div v-if="pdm.projetos.length == 0" class="estado interno">
                Nenhum projeto vinculado a este PDM.
              </div>
              <div
                v-for="projeto in pdm.projetosOrdenados"
                :key="projeto.idProjeto"
                class="cartao-projeto"
              >
                <details
                  :data-panorama-projeto-key="
                    chavePanoramaProjeto(pdm, projeto)
                  "
                  data-role="panorama-projeto"
                >
                  <summary class="cartao-projeto__cabecalho">
                    <div>
                      <h4>{{ projeto.titulo }}</h4>
                      <p class="detalhes">
                        {{
                          formatarPeriodo(projeto.dataInicio, projeto.dataFim)
                        }}
                        · {{ projeto.situacao || "Sem situação" }} · Saúde:
                        {{ projeto.nivelSaude || "—" }}
                      </p>
                    </div>
                  </summary>

                  <div class="painel-detalhes">
                    <div class="bloco">
                      <h5>Ações</h5>
                      <table v-if="projeto.acoes.length">
                        <thead>
                          <tr>
                            <th>Ação</th>
                            <th>Período</th>
                            <th>Planejado</th>
                            <th>Mensurado</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="acao in projeto.acoes" :key="acao.idMarco">
                            <td>
                              <strong>{{ acao.titulo }}</strong>
                            </td>
                            <td>
                              {{
                                formatarPeriodo(
                                  acao.dataInicio || acao.dataLimite,
                                  acao.dataFim || acao.dataLimite
                                )
                              }}
                            </td>
                            <td>{{ acao.situacaoPlanejada || "—" }}</td>
                            <td>{{ acao.situacaoMensurada || "—" }}</td>
                          </tr>
                        </tbody>
                      </table>
                      <p v-else class="estado interno">
                        Sem ações registradas para o projeto.
                      </p>
                    </div>
                    <div class="bloco">
                      <h5>KPIs</h5>
                      <table v-if="projeto.kpis.length">
                        <thead>
                          <tr>
                            <th>Indicador</th>
                            <th>Meta</th>
                            <th>Atual</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="metrica in projeto.kpis"
                            :key="metrica.idMetrica"
                          >
                            <td>{{ metrica.nome }}</td>
                            <td>{{ formatarNumero(metrica.meta) }}</td>
                            <td>{{ formatarNumero(metrica.valorAtual) }}</td>
                          </tr>
                        </tbody>
                      </table>
                      <p v-else class="estado interno">
                        Sem KPIs vinculados ao projeto.
                      </p>
                    </div>
                  </div>
                </details>
              </div>
            </details>
          </article>
        </div>
      </header>
    </section>

    <section v-else class="estado">
      Selecione filtros e gere o panorama para visualizar os dados.
    </section>

    <section v-if="erroProgresso" class="estado erro">
      {{ erroProgresso }}
    </section>
    <section v-else-if="carregandoProgresso" class="estado">
      Gerando relatório de progresso...
    </section>

    <!-- Aba Progresso -->
    <div v-show="abaAtiva === 'progresso'" class="tab-content">
      <!-- Indicador de Filtro Ativo -->
      <div
        v-if="relatorioProgresso.length || carregandoProgresso || erroProgresso"
        class="filtro-ativo-indicator"
      >
        <div class="filtro-info">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M14,12V19.88C14.04,20.18 13.94,20.5 13.71,20.71C13.32,21.1 12.69,21.1 12.3,20.71L10.29,18.7C10.06,18.47 9.96,18.16 10,17.87V12H9.97L4.21,4.62C3.87,4.19 3.95,3.56 4.38,3.22C4.57,3.08 4.78,3 5,3V3H19V3C19.22,3 19.43,3.08 19.62,3.22C20.05,3.56 20.13,4.19 19.79,4.62L14.03,12H14Z"
            />
          </svg>
          <span class="filtro-label">Filtrando por:</span>
          <span class="filtro-valor">{{ municipioFiltroTexto }}</span>
        </div>
        <div v-if="relatorioProgresso.length" class="resultados-count">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M13,2.05V5.08C16.39,5.57 19,8.47 19,12C19,15.53 16.39,18.43 13,18.92V21.95C18.05,21.45 22,17.19 22,12C22,6.82 18.05,2.55 13,2.05M11,21.95V18.92C7.61,18.43 5,15.53 5,12C5,8.47 7.61,5.57 11,5.08V2.05C5.95,2.55 2,6.81 2,12C2,17.52 6.48,22 12,22C11.66,22 11.33,21.98 11,21.95Z"
            />
          </svg>
          {{ relatorioProgresso.length }} projeto{{
            relatorioProgresso.length !== 1 ? "s" : ""
          }}
          encontrado{{ relatorioProgresso.length !== 1 ? "s" : "" }}
        </div>
      </div>

      <section v-if="erroProgresso" class="estado erro">
        {{ erroProgresso }}
      </section>
      <section v-else-if="carregandoProgresso" class="estado">
        Gerando relatório de progresso...
      </section>
      <section
        v-else-if="relatorioProgresso.length"
        class="painel painel-progresso"
      >
        <article
          v-for="projeto in relatorioProgresso"
          :key="projeto.idProjeto"
          class="cartao-progresso"
        >
          <header class="cartao-progresso__cabecalho">
            <div class="projeto-info">
              <h3>{{ projeto.titulo }}</h3>
              <div class="projeto-meta">
                <div class="meta-item">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    />
                  </svg>
                  <span>{{ projeto.situacao || "—" }}</span>
                </div>
                <div class="meta-item">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1L9 7V9L10.5 10.5C10.2 11 10 11.5 10 12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12C14 11.5 13.8 11 13.5 10.5L15 9M15 18.5C15 17.1 13.9 16 12.5 16C11.1 16 10 17.1 10 18.5C10 19.9 11.1 21 12.5 21C13.9 21 15 19.9 15 18.5Z"
                    />
                  </svg>
                  <span>{{ projeto.nivelSaude || "—" }}</span>
                </div>
              </div>
            </div>
            <div class="progresso-total">
              <div class="progresso-circle">
                <svg class="progress-ring" width="80" height="80">
                  <circle
                    class="progress-ring-circle-bg"
                    cx="40"
                    cy="40"
                    r="32"
                  />
                  <circle
                    class="progress-ring-circle"
                    cx="40"
                    cy="40"
                    r="32"
                    :style="`--progress: ${projeto.progressoPonderado || 0}`"
                  />
                </svg>
                <div class="progresso-texto">
                  <span class="valor">{{
                    formatarPercentual(projeto.progressoPonderado)
                  }}</span>
                  <span class="label">Progresso</span>
                </div>
                <div class="okr-info">
                  <small
                    >OKR:
                    {{ formatarPercentual(projeto.progressoOkr || 0) }}</small
                  >
                </div>
              </div>
            </div>
          </header>

          <div v-if="projeto.acoes.length" class="acoes-progresso">
            <div
              v-for="acao in projeto.acoes"
              :key="acao.idAcao"
              class="acao-progresso"
            >
              <header class="acao-header">
                <div class="acao-info">
                  <h4>{{ acao.titulo }}</h4>
                  <div class="acao-detalhes">
                    <div class="detalhe-item">
                      <span class="label">Peso das entregas:</span>
                      <span class="valor">{{
                        formatarPercentual(acao.pesoTotalEntregas)
                      }}</span>
                    </div>
                    <div class="detalhe-item">
                      <span class="label">Progresso informado:</span>
                      <span class="valor">{{
                        formatarPercentual(acao.progressoInformado)
                      }}</span>
                    </div>
                  </div>
                </div>
                <div class="acao-progresso-badge">
                  <span class="progresso-valor">{{
                    formatarPercentual(acao.progressoCalculado)
                  }}</span>
                  <div class="progresso-barra">
                    <div
                      class="progresso-fill"
                      :style="`width: ${Math.min(
                        acao.progressoCalculado || 0,
                        100
                      )}%`"
                    ></div>
                  </div>
                </div>
              </header>

              <div v-if="acao.entregas.length" class="entregas-container">
                <div
                  v-for="entrega in acao.entregas"
                  :key="entrega.idEntrega"
                  class="entrega-item"
                >
                  <div class="entrega-header">
                    <div class="entrega-info">
                      <h5>{{ entrega.titulo }}</h5>
                      <div class="entrega-stats">
                        <span class="stat">
                          <span class="stat-label">Peso:</span>
                          <span class="stat-value">{{
                            formatarPercentual(entrega.peso)
                          }}</span>
                        </span>
                        <span class="stat">
                          <span class="stat-label">Conclusão:</span>
                          <span class="stat-value">{{
                            formatarPercentual(entrega.percentualConclusao)
                          }}</span>
                        </span>
                        <span class="stat stat-primary">
                          <span class="stat-label">Ponderado:</span>
                          <span class="stat-value">{{
                            formatarPercentual(entrega.progressoCalculado)
                          }}</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div v-if="entrega.tarefas.length" class="tarefas-container">
                    <div
                      v-for="tarefa in entrega.tarefas"
                      :key="tarefa.idTarefa"
                      class="tarefa-item"
                    >
                      <div class="tarefa-info">
                        <span class="tarefa-nome">{{ tarefa.titulo }}</span>
                        <div class="tarefa-stats">
                          <span class="tarefa-stat"
                            >Peso: {{ formatarPercentual(tarefa.peso) }}</span
                          >
                          <span class="tarefa-stat"
                            >Conclusão:
                            {{
                              formatarPercentual(tarefa.percentualConclusao)
                            }}</span
                          >
                        </div>
                      </div>
                      <div class="tarefa-progresso">
                        <div class="mini-barra">
                          <div
                            class="mini-fill"
                            :style="`width: ${Math.min(
                              tarefa.percentualConclusao || 0,
                              100
                            )}%`"
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="estado interno">
                    Sem tarefas vinculadas.
                  </div>
                </div>
              </div>
              <div v-else class="estado interno">Sem entregas registradas.</div>
            </div>
          </div>
          <div v-else class="estado interno">
            Sem ações cadastradas para este projeto.
          </div>
        </article>
      </section>
      <section v-else class="estado">
        Gere o relatório de progresso para visualizar a distribuição ponderada.
      </section>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ComboboxMunicipio from "@/modules/city/components/ComboboxSingle.vue";
import {
  gerarRelatorioPanorama,
  gerarRelatorioProgresso,
} from "../services/api";

export default {
  name: "RelatoriosGestaoProjetos",
  components: { ComboboxMunicipio },
  data() {
    return {
      abaAtiva: "panorama",
      carregando: false,
      erro: "",
      carregandoProgresso: false,
      erroProgresso: "",
      ordenacaoPanorama: "alfabetica",
      ordenacaoProgresso: "alfabetica",
      filtros: { idMunicipio: undefined },
      relatorio: [],
      relatorioProgresso: [],
      municipioFiltro: { municipio: null },
    };
  },
  computed: {
    ...mapGetters(["uf"]),
    relatorioOrdenado() {
      const pdms = (this.relatorio || []).map((pdm) => {
        const projetos = [...(pdm.projetos || [])];
        projetos.sort((a, b) =>
          this.compararProjeto(a, b, this.ordenacaoPanorama)
        );
        return { ...pdm, projetosOrdenados: projetos };
      });
      pdms.sort((a, b) => this.compararPdm(a, b, this.ordenacaoPanorama));
      return pdms;
    },
    progressoAgrupado() {
      const grupos = {};
      (this.relatorioProgresso || []).forEach((projeto) => {
        const nomePdm = projeto.nomePdm || "Sem PDM";
        const chave = `${projeto.idPdm || "sem"}-${nomePdm}`;
        if (!grupos[chave]) {
          grupos[chave] = {
            idPdm: projeto.idPdm || null,
            nomePdm,
            projetos: [],
          };
        }
        grupos[chave].projetos.push(projeto);
      });
      const lista = Object.values(grupos);
      lista.forEach((g) =>
        g.projetos.sort((a, b) =>
          this.compararProjeto(a, b, this.ordenacaoProgresso)
        )
      );
      lista.sort((a, b) => this.compararPdm(a, b, this.ordenacaoProgresso));
      return lista;
    },
    municipioFiltroTexto() {
      if (
        this.municipioFiltro.municipio &&
        this.municipioFiltro.municipio.nome
      ) {
        return this.municipioFiltro.municipio.nome;
      }
      return "Todos os municípios";
    },
  },
  async created() {
    this.tentarSincronizarMunicipio();
  },
  watch: {
    "filtros.idMunicipio": {
      handler() {
        this.$nextTick(() => this.tentarSincronizarMunicipio());
      },
    },
  },
  async created() {
    this.tentarSincronizarMunicipio();
  },
  methods: {
    compararTexto(a, b) {
      return String(a || "").localeCompare(String(b || ""), "pt-BR", {
        sensitivity: "base",
      });
    },
    compararPdm(a, b, modo) {
      if (modo === "progresso") {
        const progA = this.mediaProgressoPdm(a);
        const progB = this.mediaProgressoPdm(b);
        if (progB !== progA) return progB - progA;
      }
      return this.compararTexto(a.nome || a.nomePdm, b.nome || b.nomePdm);
    },
    compararProjeto(a, b, modo) {
      if (modo === "progresso") {
        const progA = Number(a.progressoPonderado || 0);
        const progB = Number(b.progressoPonderado || 0);
        if (progB !== progA) return progB - progA;
      }
      return this.compararTexto(a.titulo, b.titulo);
    },
    mediaProgressoPdm(pdm) {
      const projetos = pdm.projetos || pdm.projetosOrdenados || [];
      if (!projetos.length) return 0;
      return (
        projetos.reduce(
          (acc, p) => acc + Number(p.progressoPonderado || 0),
          0
        ) / projetos.length
      );
    },
    aoAlterarMunicipio(municipio) {
      if (Array.isArray(municipio)) {
        const selecionado = municipio.find(
          (item) => item && (item.municipioId || item.id)
        );
        this.filtros.idMunicipio = selecionado
          ? Number(selecionado.municipioId || selecionado.id)
          : undefined;
      } else if (municipio && typeof municipio === "object") {
        this.filtros.idMunicipio =
          municipio.municipioId || municipio.id || undefined;
      } else {
        this.filtros.idMunicipio = municipio || undefined;
      }
      this.municipioFiltro.municipio =
        municipio && typeof municipio === "object" ? municipio : null;
    },
    tentarSincronizarMunicipio(tentativa = 0) {
      if (tentativa > 10) return;
      if (!this.filtros.idMunicipio) {
        this.municipioFiltro.municipio = null;
        return;
      }
      const combobox = this.$refs.filtroMunicipio;
      const lista = combobox?.municipios;
      if (Array.isArray(lista) && lista.length) {
        const encontrado = lista.find(
          (item) => item.municipioId === this.filtros.idMunicipio
        );
        if (encontrado) {
          this.municipioFiltro.municipio = encontrado;
          return;
        }
      }
      setTimeout(() => this.tentarSincronizarMunicipio(tentativa + 1), 200);
    },
    alternarAba(aba) {
      this.abaAtiva = aba;
      // Limpa erros quando troca de aba
      if (aba === "panorama") {
        this.erroProgresso = "";
      } else {
        this.erro = "";
      }
    },
    limparFiltros() {
      this.filtros.idMunicipio = undefined;
      this.municipioFiltro.municipio = null;
    },
    async carregarRelatorio() {
      this.carregando = true;
      this.erro = "";
      try {
        const parametros = { ...this.filtros };
        if (!parametros.idMunicipio) delete parametros.idMunicipio;
        const { data } = await gerarRelatorioPanorama(parametros);
        this.relatorio = Array.isArray(data) ? data : [];
        if (!this.relatorio.length)
          this.erro = "Nenhum dado encontrado com os filtros selecionados.";
      } catch (error) {
        this.erro = "Falha ao gerar o relatório. Tente novamente em instantes.";
      } finally {
        this.carregando = false;
      }
    },
    async carregarRelatorioProgresso() {
      this.carregandoProgresso = true;
      this.erroProgresso = "";
      try {
        const parametros = { ...this.filtros };
        if (!parametros.idMunicipio) delete parametros.idMunicipio;
        const { data } = await gerarRelatorioProgresso(parametros);
        this.relatorioProgresso = Array.isArray(data) ? data : [];
        if (!this.relatorioProgresso.length)
          this.erroProgresso =
            "Nenhum dado de progresso encontrado com os filtros selecionados.";
      } catch (error) {
        this.erroProgresso =
          "Falha ao gerar o relatório de progresso. Tente novamente em instantes.";
        this.relatorioProgresso = [];
      } finally {
        this.carregandoProgresso = false;
      }
    },
    formatarData(valor) {
      if (!valor) return "—";
      const data = new Date(valor);
      if (Number.isNaN(data.getTime())) return "—";
      return data.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    formatarPeriodo(inicio, fim) {
      if (!inicio && !fim) return "—";
      const i = this.formatarData(inicio);
      const f = this.formatarData(fim);
      if (!inicio) return `Até ${f}`;
      if (!fim) return `A partir de ${i}`;
      return `${i} - ${f}`;
    },
    formatarNumero(valor) {
      if (valor === null || valor === undefined || valor === "") return "—";
      return Number(valor).toLocaleString("pt-BR", {
        maximumFractionDigits: 2,
      });
    },
    formatarPercentual(valor) {
      if (valor === null || valor === undefined || valor === "") return "—";
      return `${Number(valor).toLocaleString("pt-BR", {
        maximumFractionDigits: 2,
      })}%`;
    },
    resumoPdm(pdm) {
      const municipio = pdm.nomeMunicipio
        ? `${pdm.nomeMunicipio} (${pdm.siglaEstado || "UF"})`
        : pdm.siglaEstado || "—";
      return `Município: ${municipio}`;
    },
    chavePanoramaPdm(pdm) {
      return `${pdm.idPdm || "sem"}-${pdm.nome || "Sem PDM"}`;
    },
    chavePanoramaProjeto(pdm, projeto) {
      return `${this.chavePanoramaPdm(pdm)}-${projeto.idProjeto || "sem"}`;
    },
    chavePdm(grupo) {
      return `${grupo.idPdm || "sem"}-${grupo.nomePdm || "Sem PDM"}`;
    },
    chaveProjeto(projeto) {
      return `${projeto.idProjeto || "sem"}-${projeto.titulo || "Sem título"}`;
    },
    chaveObjetivo(projeto, objetivo) {
      return `${this.chaveProjeto(projeto)}-${
        objetivo.idObjetivo || objetivo.texto || "sem-objetivo"
      }`;
    },
    chaveArea(projeto, area) {
      return `${this.chaveProjeto(projeto)}-${area.idEixo || "sem-eixo"}`;
    },
    chaveAcao(projeto, acao) {
      return `${this.chaveProjeto(projeto)}-${
        acao.idAcao || acao.titulo || "sem-acao"
      }`;
    },
    chaveEntrega(projeto, acao, entrega) {
      return `${this.chaveAcao(projeto, acao)}-${
        entrega.idEntrega || entrega.titulo || "sem-entrega"
      }`;
    },
    agruparObjetivosPorArea(projeto) {
      const grupos = {};
      (projeto.objetivosOkr || []).forEach((objetivo) => {
        const idEixo = objetivo.idEixo || "sem-eixo";
        if (!grupos[idEixo]) {
          grupos[idEixo] = {
            idEixo,
            nome: `Área Chave ${idEixo}`,
            objetivos: [],
          };
        }
        grupos[idEixo].objetivos.push(objetivo);
      });
      return Object.values(grupos);
    },
    escaparAtributo(valor) {
      return String(valor || "")
        .replace(/\\/g, "\\\\")
        .replace(/"/g, '\\"');
    },
    estaExpandidoPorSeletor(seletor) {
      const elemento = this.$el?.querySelector(seletor);
      return !!elemento?.open;
    },
    exportarPanoramaXls() {
      const rows = [];
      this.relatorioOrdenado.forEach((pdm) =>
        pdm.projetosOrdenados.forEach((p) =>
          rows.push({
            PDM: pdm.nome,
            Projeto: p.titulo,
            Situacao: p.situacao || "",
            Saude: p.nivelSaude || "",
          })
        )
      );
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Panorama");
      const out = XLSX.write(wb, { type: "array", bookType: "xlsx" });
      saveAs(
        new Blob([out], { type: "application/octet-stream" }),
        "panorama-gestao-projetos.xlsx"
      );
    },
    exportarProgressoXls() {
      const rows = [];
      this.progressoAgrupado.forEach((g) =>
        g.projetos.forEach((p) =>
          rows.push({
            PDM: g.nomePdm,
            Projeto: p.titulo,
            Progresso: Number(p.progressoPonderado || 0),
          })
        )
      );
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Progresso");
      const out = XLSX.write(wb, { type: "array", bookType: "xlsx" });
      saveAs(
        new Blob([out], { type: "application/octet-stream" }),
        "progresso-gestao-projetos.xlsx"
      );
    },
    criarPdfCabecalho(doc, titulo) {
      doc.setFillColor(29, 78, 216);
      doc.rect(0, 0, 210, 30, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.text(titulo, 14, 18);
      doc.setFontSize(10);
      doc.text(`Emitido em ${new Date().toLocaleString("pt-BR")}`, 14, 24);
      doc.setTextColor(20, 20, 20);
    },
    exportarPanoramaPdf() {
      const doc = new jsPDF("p", "mm", "a4");
      this.criarPdfCabecalho(doc, "Relatório Panorama - Gestão de Projetos");
      const body = [];
      this.relatorioOrdenado.forEach((pdm) => {
        const pdmKey = this.escaparAtributo(this.chavePanoramaPdm(pdm));
        const pdmExpandido = this.estaExpandidoPorSeletor(
          `[data-role='panorama-pdm'][data-panorama-pdm-key="${pdmKey}"]`
        );
        pdm.projetosOrdenados.forEach((projeto) => {
          body.push([
            pdm.nome,
            projeto.titulo,
            projeto.situacao || "-",
            projeto.nivelSaude || "-",
          ]);
          if (!pdmExpandido) return;
          const projetoKey = this.escaparAtributo(
            this.chavePanoramaProjeto(pdm, projeto)
          );
          const projetoExpandido = this.estaExpandidoPorSeletor(
            `[data-role='panorama-projeto'][data-panorama-projeto-key="${projetoKey}"]`
          );
          if (!projetoExpandido) return;
          (projeto.acoes || []).forEach((acao) => {
            body.push([
              "",
              `Ação: ${acao.titulo || "-"}`,
              acao.situacaoMensurada || acao.situacaoPlanejada || "-",
              this.formatarPercentual(
                acao.progressoMensurado || acao.progressoPlanejado || 0
              ),
            ]);
          });
          (projeto.kpis || []).forEach((kpi) => {
            body.push([
              "",
              `Métrica: ${kpi.nome || "-"}`,
              `Meta ${this.formatarNumero(kpi.meta)}`,
              `Atual ${this.formatarNumero(
                kpi.valorAtual || kpi.valorMensurado
              )}`,
            ]);
          });
        });
      });
      autoTable(doc, {
        startY: 36,
        head: [["PDM", "Projeto", "Situação", "Saúde"]],
        body,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [15, 23, 42] },
        alternateRowStyles: { fillColor: [241, 245, 249] },
      });
      doc.save("panorama-gestao-projetos.pdf");
    },
    exportarProgressoPdf() {
      const doc = new jsPDF("p", "mm", "a4");
      this.criarPdfCabecalho(doc, "Relatório Progresso - Gestão de Projetos");
      const body = [];

      this.progressoAgrupado.forEach((grupo) => {
        const pdmKey = this.escaparAtributo(this.chavePdm(grupo));
        const pdmExpandido = this.estaExpandidoPorSeletor(
          `[data-role='progresso-pdm'][data-pdm-key="${pdmKey}"]`
        );
        grupo.projetos.forEach((projeto) => {
          body.push([
            grupo.nomePdm,
            projeto.titulo,
            `${Number(projeto.progressoPonderado || 0).toFixed(2)}%`,
          ]);

          if (!pdmExpandido) return;
          const projetoKey = this.escaparAtributo(this.chaveProjeto(projeto));
          const projetoExpandido = this.estaExpandidoPorSeletor(
            `[data-role='progresso-projeto'][data-projeto-key="${projetoKey}"]`
          );
          if (!projetoExpandido) return;

          const acoesAbertas = this.estaExpandidoPorSeletor(
            `[data-role='progresso-acoes'][data-projeto-acoes-key="${projetoKey}"]`
          );
          if (acoesAbertas) {
            (projeto.acoes || []).forEach((acao) => {
              body.push([
                "",
                `Ação: ${acao.titulo || "—"}`,
                this.formatarPercentual(acao.progressoCalculado || 0),
              ]);
              const acaoKey = this.escaparAtributo(
                this.chaveAcao(projeto, acao)
              );
              const acaoExpandida = this.estaExpandidoPorSeletor(
                `[data-role='progresso-acao'][data-acao-key="${acaoKey}"]`
              );
              if (!acaoExpandida) return;
              (acao.entregas || []).forEach((entrega) => {
                body.push([
                  "",
                  `  Entrega: ${entrega.titulo || "—"}`,
                  this.formatarPercentual(
                    entrega.progressoCalculado ||
                      entrega.percentualConclusao ||
                      0
                  ),
                ]);
                const entregaKey = this.escaparAtributo(
                  this.chaveEntrega(projeto, acao, entrega)
                );
                const entregaExpandida = this.estaExpandidoPorSeletor(
                  `[data-role='progresso-entrega'][data-entrega-key="${entregaKey}"]`
                );
                if (!entregaExpandida) return;
                (entrega.tarefas || []).forEach((tarefa) => {
                  body.push([
                    "",
                    `    Tarefa: ${tarefa.titulo || "—"}`,
                    this.formatarPercentual(tarefa.percentualConclusao || 0),
                  ]);
                });
              });
            });
          }

          const metricasAbertas = this.estaExpandidoPorSeletor(
            `[data-role='progresso-metricas'][data-projeto-metricas-key="${projetoKey}"]`
          );
          if (metricasAbertas) {
            (projeto.metricas || projeto.kpis || []).forEach((metrica) => {
              body.push([
                "",
                `Métrica: ${metrica.nome || metrica.titulo || "—"}`,
                `Meta ${this.formatarNumero(
                  metrica.meta
                )} | Atual ${this.formatarNumero(
                  metrica.valorAtual || metrica.valorMensurado
                )}`,
              ]);
            });
          }

          const okrAbertos = this.estaExpandidoPorSeletor(
            `[data-role='progresso-okr'][data-projeto-okr-key="${projetoKey}"]`
          );
          if (!okrAbertos) return;

          this.agruparObjetivosPorArea(projeto).forEach((area) => {
            body.push([
              "",
              `Área Chave: ${area.nome}`,
              `${area.objetivos.length} objetivo(s)`,
            ]);
            const areaKey = this.escaparAtributo(this.chaveArea(projeto, area));
            const areaExpandida = this.estaExpandidoPorSeletor(
              `[data-role='progresso-area'][data-area-key="${areaKey}"]`
            );
            if (!areaExpandida) return;
            area.objetivos.forEach((objetivo) => {
              body.push([
                "",
                `  Objetivo: ${objetivo.texto || "—"}`,
                this.formatarPercentual(objetivo.progresso || 0),
              ]);
              const objetivoKey = this.escaparAtributo(
                this.chaveObjetivo(projeto, objetivo)
              );
              const objetivoExpandido = this.estaExpandidoPorSeletor(
                `[data-role='progresso-objetivo'][data-objetivo-key="${objetivoKey}"]`
              );
              if (!objetivoExpandido) return;
              (objetivo.resultados || []).forEach((resultado) => {
                body.push([
                  "",
                  `    Resultado Estratégico: ${resultado.texto || "—"}`,
                  this.formatarPercentual(resultado.percentualAtingido || 0),
                ]);
              });
            });
          });
        });
      });

      autoTable(doc, {
        startY: 36,
        head: [["PDM", "Projeto", "Progresso"]],
        body,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [15, 23, 42] },
        alternateRowStyles: { fillColor: [241, 245, 249] },
      });
      doc.save("progresso-gestao-projetos.pdf");
    },
  },
};
</script>

<style scoped>
.relatorios {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.cabecalho {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}
.cabecalho h2 {
  margin: 0;
  font-size: 1.8rem;
  color: #0f172a;
}
.acoes button {
  border-radius: 8px;
  border: 1px solid #1d4ed8;
  background: #1d4ed8;
  color: #ffffff;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: 600;
}
.acoes button:disabled {
  opacity: 0.6;
  cursor: wait;
}
.painel-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.painel-toolbar select {
  margin-left: 8px;
}
.botao-exportar {
  border-radius: 8px;
  border: 1px solid #0f172a;
  background: #fff;
  padding: 8px 12px;
  margin-left: 8px;
  cursor: pointer;
}
.filtros {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  background: #ffffff;
  border: 1px solid #d6dde9;
  border-radius: 12px;
  padding: 16px;
}
.campo :deep(.v-input__slot),
.campo :deep(.v-field) {
  border: 1px solid #c7d2e2;
  border-radius: 8px;
  background: #f8fafc;
}
.estado {
  background: #ffffff;
  border: 1px solid #d6dde9;
  border-radius: 12px;
  padding: 18px;
  color: #1f2937;
}
.estado.erro {
  border-color: #fecaca;
  color: #b91c1c;
}
.estado.interno {
  background: #f1f5f9;
  border: none;
  padding: 12px;
}
.painel {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.cartao-pdm {
  background: #ffffff;
  border: 1px solid #d6dde9;
  border-radius: 12px;
  padding: 18px;
  margin-top: 20px;
}
summary {
  list-style: none;
  cursor: pointer;
}
summary::-webkit-details-marker {
  display: none;
}
.cartao-pdm__cabecalho {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
}
.cartao-pdm__metadados {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin: 0;
}
.cartao-projeto,
.cartao-progresso {
  border: 1px solid #d6dde9;
  border-radius: 10px;
  padding: 14px;
  margin-top: 12px;
}
.cartao-projeto__cabecalho,
.cartao-progresso__cabecalho {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
}
.painel-detalhes {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
  margin-top: 10px;
}
.bloco {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  background: #f8fafc;
}
.bloco table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}
.bloco th,
.bloco td {
  text-align: left;
  padding: 8px;
  border-bottom: 1px solid #e2e8f0;
}
.progresso-total {
  text-align: right;
  color: #0f172a;
}
.progresso-total strong {
  font-size: 1.2rem;
  color: #1d4ed8;
}
.acoes-progresso {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}
.acao-progresso {
  border: 1px solid #dbeafe;
  border-radius: 10px;
  background: #f8fbff;
  padding: 12px;
}
.acao-progresso header {
  display: flex;
  justify-content: space-between;
}
.secao-expandivel {
  margin-top: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  background: #f8fafc;
}
.secao-expandivel > summary h4 {
  display: inline;
  margin: 0;
}
.subnivel-lista {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}
.linha-expandida {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  padding: 8px 10px;
  background: #fff;
}
.okr-progresso {
  margin-top: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  background: #f8fafc;
}
.okr-progresso h4 {
  margin: 0 0 8px 0;
}
.okr-objetivo {
  border: 1px solid #dbeafe;
  border-radius: 8px;
  padding: 8px 10px;
  background: #fff;
  margin-top: 8px;
}
.okr-objetivo__cabecalho {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
}
.okr-resultados {
  list-style: disc;
  margin: 8px 0 0 16px;
  padding: 0;
}
.okr-resultados li {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 0;
}
.relatorios {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Sistema de Abas */
.relatorios-tabs {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.tabs-nav {
  display: flex;
  gap: 4px;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  background: transparent;
  color: #64748b;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  white-space: nowrap;
}

.tab-btn:hover {
  color: #374151;
  background: rgba(255, 255, 255, 0.7);
}

.tab-btn.active {
  background: #ffffff;
  color: #0074cc;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
}

.tab-btn svg {
  transition: color 0.3s ease;
}

.tab-btn.active svg {
  color: #0074cc;
}

.tab-badge {
  background: #0074cc;
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.tab-btn:not(.active) .tab-badge {
  background: #94a3b8;
}

.tabs-actions {
  display: flex;
  gap: 12px;
}

.tabs-content {
  min-height: 200px;
}

/* Indicador de Filtro Ativo */
.filtro-ativo-indicator {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.filtro-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.filtro-info svg {
  color: #0074cc;
}

.filtro-label {
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 500;
}

.filtro-valor {
  color: #1e293b;
  font-weight: 700;
  font-size: 0.95rem;
  background: #ffffff;
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.resultados-count {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #0074cc;
  font-weight: 600;
  font-size: 0.85rem;
  background: #dbeafe;
  padding: 6px 12px;
  border-radius: 20px;
  border: 1px solid #93c5fd;
}

.resultados-count svg {
  color: #1d4ed8;
}

@media (max-width: 768px) {
  .filtro-ativo-indicator {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .filtro-info {
    justify-content: center;
  }

  .resultados-count {
    justify-content: center;
  }
}

.tab-content {
  animation: fadeIn 0.3s ease-in-out;
  width: 100%;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Melhorias nos botões */
.botao-primario,
.botao-secundario {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  white-space: nowrap;
}

.botao-primario {
  background: linear-gradient(135deg, #0074cc 0%, #2563eb 100%);
  color: white;
}

.botao-primario:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 116, 204, 0.3);
}

.botao-primario:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.botao-secundario {
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
}

.botao-secundario:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
}

.botao-secundario:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsividade */
@media (max-width: 768px) {
  .relatorios-tabs {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .tabs-nav {
    justify-content: center;
  }

  .tab-btn {
    flex: 1;
    justify-content: center;
    padding: 10px 12px;
    font-size: 0.85rem;
  }

  .tabs-actions {
    justify-content: center;
  }
}

.cabecalho {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.cabecalho h2 {
  margin: 0;
  font-size: 1.8rem;
  color: #0f172a;
}

.acoes button {
  border-radius: 8px;
  border: 1px solid #1d4ed8;
  background: #1d4ed8;
  color: #ffffff;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: 600;
}

.acoes button:disabled {
  opacity: 0.6;
  cursor: wait;
}

.painel-progresso {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cartao-progresso {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cartao-progresso__cabecalho {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
}

/* Cartão de Progresso */
.cartao-progresso {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.cartao-progresso::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #059669 0%, #10b981 50%, #34d399 100%);
}

.cartao-progresso:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

/* Cabeçalho do Cartão */
.cartao-progresso__cabecalho {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f1f5f9;
}

.projeto-info h3 {
  margin: 0 0 12px 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
}

.projeto-meta {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f9;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 500;
}

.meta-item svg {
  color: #0074cc;
}

/* Círculo de Progresso */
.progresso-total {
  position: relative;
}

.progresso-circle {
  position: relative;
  width: 80px;
  height: 80px;
}

.progress-ring {
  transform: rotate(-90deg);
}

.progress-ring-circle-bg {
  stroke: #e2e8f0;
  stroke-width: 6;
  fill: transparent;
}

.progress-ring-circle {
  stroke: #059669;
  stroke-width: 6;
  fill: transparent;
  stroke-linecap: round;
  stroke-dasharray: 201;
  stroke-dashoffset: calc(201 - (201 * var(--progress)) / 100);
  transition: stroke-dashoffset 1s ease-in-out;
}

.progresso-texto {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.progresso-texto .valor {
  display: block;
  font-size: 1.1rem;
  font-weight: 700;
  color: #059669;
  line-height: 1;
}

.progresso-texto .label {
  display: block;
  font-size: 0.65rem;
  color: #64748b;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.05em;
  margin-top: 2px;
}

/* Ações */
.acoes-progresso {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.acao-progresso {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.acao-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
}

.acao-info h4 {
  margin: 0 0 8px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
}

.acao-detalhes {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.detalhe-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detalhe-item .label {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.detalhe-item .valor {
  font-size: 0.9rem;
  color: #0f172a;
  font-weight: 700;
}

.acao-progresso-badge {
  text-align: center;
  min-width: 120px;
}

.progresso-valor {
  display: block;
  font-size: 1.2rem;
  font-weight: 700;
  color: #0074cc;
  margin-bottom: 8px;
}

.progresso-barra {
  width: 100%;
  height: 8px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
}

.progresso-fill {
  height: 100%;
  background: linear-gradient(90deg, #0074cc 0%, #2563eb 100%);
  border-radius: 4px;
  transition: width 0.8s ease;
}

/* Entregas */
.entregas-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.entrega-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
}

.entrega-header {
  margin-bottom: 12px;
}

.entrega-info h5 {
  margin: 0 0 8px 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
}

.entrega-stats {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.stat {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.8rem;
}

.stat-primary {
  background: #dbeafe;
  border-color: #93c5fd;
}

.stat-label {
  color: #64748b;
  font-weight: 500;
}

.stat-value {
  color: #0f172a;
  font-weight: 700;
}

.stat-primary .stat-value {
  color: #1e40af;
}

/* Tarefas */
.tarefas-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.tarefa-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
}

.tarefa-item:last-child {
  border-bottom: none;
}

.tarefa-info {
  flex: 1;
}

.tarefa-nome {
  display: block;
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
  margin-bottom: 4px;
}

.tarefa-stats {
  display: flex;
  gap: 12px;
}

.tarefa-stat {
  font-size: 0.75rem;
  color: #64748b;
}

.tarefa-progresso {
  width: 60px;
  margin-left: 16px;
}

.mini-barra {
  width: 100%;
  height: 6px;
  background: #f1f5f9;
  border-radius: 3px;
  overflow: hidden;
}

.mini-fill {
  height: 100%;
  background: linear-gradient(90deg, #10b981 0%, #34d399 100%);
  border-radius: 3px;
  transition: width 0.6s ease;
}

/* Estados vazios melhorados */
.estado.interno {
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  color: #64748b;
  font-style: italic;
}

/* Responsividade */
@media (max-width: 768px) {
  .cartao-progresso__cabecalho {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .progresso-total {
    align-self: center;
  }

  .acao-header {
    flex-direction: column;
    gap: 12px;
  }

  .acao-progresso-badge {
    align-self: flex-start;
  }

  .entrega-stats,
  .acao-detalhes {
    flex-direction: column;
    gap: 8px;
  }

  .tarefa-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .tarefa-progresso {
    width: 100%;
    margin-left: 0;
  }
}

.progresso-total {
  text-align: right;
  color: #0f172a;
}

.filtros-container {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
}

.filtros-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 16px;
}

.filtros-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
}

.filtros-header p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.4;
}

.filtros-clear {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #64748b;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.filtros-clear:hover {
  background: #e2e8f0;
  color: #475569;
  border-color: #cbd5e1;
}

.filtros-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.filtro-campo {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filtro-campo label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
}

.filtro-campo label svg {
  color: #0074cc;
}

.filtro-input-wrapper {
  position: relative;
}

.filtro-campo :deep(.v-input__control),
.filtro-campo :deep(.v-field) {
  border-radius: 10px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.filtro-campo :deep(.v-field:hover) {
  border-color: #0074cc;
  box-shadow: 0 2px 6px rgba(0, 116, 204, 0.1);
}

.filtro-campo :deep(.v-field--focused) {
  border-color: #0074cc;
  box-shadow: 0 0 0 3px rgba(0, 116, 204, 0.1);
}

.filtro-campo :deep(.v-field__input) {
  padding: 12px 16px;
  font-size: 0.95rem;
}

.filtro-campo :deep(.combobox-municipio .v-field) {
  min-height: 48px;
}

.filtro-campo :deep(.combobox-municipio .v-field__input),
.filtro-campo :deep(.combobox-municipio input) {
  min-height: 48px;
  padding: 8px 16px;
}

.filtros {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  background: #ffffff;
  border: 1px solid #d6dde9;
  border-radius: 12px;
  padding: 16px;
}

.campo {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.campo label {
  font-size: 0.85rem;
  color: #334155;
  font-weight: 600;
}

.campo input,
.campo select {
  border: 1px solid #c7d2e2;
  border-radius: 8px;
  padding: 8px 10px;
  background: #f8fafc;
  font: inherit;
}

.campo :deep(.v-input__slot),
.campo :deep(.v-field) {
  border: 1px solid #c7d2e2;
  border-radius: 8px;
  background: #f8fafc;
}

.campo :deep(.combobox-municipio .v-field) {
  min-height: 44px;
}

.campo :deep(.combobox-municipio .v-field__input),
.campo :deep(.combobox-municipio input) {
  min-height: 44px;
  padding: 6px 12px;
}

.estado {
  background: #ffffff;
  border: 1px solid #d6dde9;
  border-radius: 12px;
  padding: 18px;
  color: #1f2937;
}

.estado.erro {
  border-color: #fecaca;
  color: #b91c1c;
}

.estado.interno {
  background: #f1f5f9;
  border: none;
  padding: 12px;
}

.painel {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.cartao-pdm {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.cartao-pdm::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #0074cc 0%, #2563eb 100%);
}

.cartao-pdm:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border-color: #cbd5e1;
}

.cartao-pdm__cabecalho {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
}

.cartao-pdm__cabecalho h3 {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
}

.cartao-pdm__cabecalho .subtitulo {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 0.95rem;
  line-height: 1.4;
}

.cartao-pdm__metadados {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 0;
}

.cartao-pdm__metadados > div {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 80px;
}

.cartao-pdm__metadados dt {
  font-size: 0.7rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
}

.cartao-pdm__metadados dd {
  font-size: 0.85rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.cartao-pdm__metadados > div:nth-child(3) dd {
  color: #0074cc;
}

.cartao-projeto {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 8px 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  position: relative;
}

.cartao-projeto::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #0074cc 0%, #60a5fa 100%);
  border-radius: 2px 0 0 2px;
}

.cartao-projeto:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateX(4px);
}

.cartao-projeto__cabecalho {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
}

.cartao-projeto__cabecalho h4 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.4;
}

.cartao-projeto__cabecalho .detalhes {
  margin: 6px 0 0;
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.4;
}

.cartao-projeto__cabecalho .detalhes.secundario {
  color: #94a3b8;
  font-size: 0.8rem;
  margin-top: 3px;
}

.mensuracao {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  background: linear-gradient(135deg, #dbeafe 0%, #f0f9ff 100%);
  border: 1px solid #93c5fd;
  border-radius: 8px;
  text-align: right;
  min-width: 160px;
}

.mensuracao__titulo {
  font-size: 0.65rem;
  color: #1e40af;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.05em;
  margin-bottom: 2px;
}

.mensuracao strong {
  font-size: 0.9rem;
  color: #1e3a8a;
  font-weight: 700;
  line-height: 1.2;
}

.mensuracao small {
  font-size: 0.7rem;
  color: #3730a3;
  margin-top: 2px;
}

.painel-detalhes {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
  margin-top: 8px;
}

@media (max-width: 768px) {
  .painel-detalhes {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

.bloco {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
}

.bloco h5 {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e2e8f0;
}

.bloco h5::before {
  content: "";
  width: 4px;
  height: 16px;
  background: #0074cc;
  border-radius: 2px;
}

.bloco table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
}

.bloco th,
.bloco td {
  text-align: left;
  padding: 12px 10px;
  border-bottom: 1px solid #f1f5f9;
}

.bloco th {
  font-weight: 700;
  color: #374151;
  background: #f8fafc;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.bloco td {
  background: #ffffff;
  transition: background-color 0.2s ease;
}

.bloco tbody tr:hover td {
  background: #f8fafc;
}

.bloco td small {
  display: block;
  color: #94a3b8;
  margin-top: 2px;
  font-size: 0.75rem;
}

.bloco td strong {
  color: #1e293b;
  font-weight: 600;
}
</style>
